import React from 'react';
import { FONTS } from '../../styles/tokens';
import { GitHubConnector, type GitHubSelection } from './GitHubConnector';
import { PrimaryButton, SecondaryButton } from './DeployFormCards';

interface Colors { text: string; textSecondary: string; card: string; border: string }

interface GitHubSourceCardProps {
  colors: Colors;
  token: string;
  selection: GitHubSelection | null;
  onSelect: (selection: GitHubSelection | null) => void;
  onBack: () => void;
  onContinue: () => void;
}

/**
 * Card variant of the Upload step when the user picks the GitHub source
 * tab. Hosts the installation/repo/branch pickers and a Continue button
 * that jumps straight to the review step (skipping the secret-capture
 * step, which requires a local zip to scan).
 */
export function GitHubSourceCard(props: GitHubSourceCardProps): React.JSX.Element {
  return (
    <div style={{ padding: '24px', borderRadius: '12px', border: `1px solid ${props.colors.border}`, backgroundColor: props.colors.card }}>
      <GitHubConnector token={props.token} colors={props.colors} onSelect={props.onSelect} />
      <div className="vx-action-row" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
        <SecondaryButton colors={props.colors} onClick={props.onBack}>Back</SecondaryButton>
        <PrimaryButton onClick={props.onContinue} disabled={!props.selection} isDark={false}>
          Continue to review
        </PrimaryButton>
      </div>
    </div>
  );
}

interface GitReviewCardProps {
  colors: Colors;
  isDark: boolean;
  name: string;
  description: string;
  team: string;
  category: string;
  selection: GitHubSelection;
  building: boolean;
  error: string | null;
  onDeploy: () => void;
  onChangeSource: () => void;
  onEditDetails: () => void;
}

/** Review step for the GitHub source — shows the selected repo/branch. */
export function GitReviewCard(props: GitReviewCardProps): React.JSX.Element {
  return (
    <div style={{ padding: '24px', borderRadius: '12px', border: `1px solid ${props.colors.border}`, backgroundColor: props.colors.card }}>
      <ReviewRow colors={props.colors} label="Name" value={props.name} onEdit={props.onEditDetails} />
      {props.description && <ReviewRow colors={props.colors} label="Description" value={props.description} onEdit={props.onEditDetails} />}
      <ReviewRow colors={props.colors} label="Team" value={props.team || '—'} onEdit={props.onEditDetails} />
      <ReviewRow colors={props.colors} label="Category" value={props.category} onEdit={props.onEditDetails} />
      <ReviewRow colors={props.colors} label="Repository" value={`${props.selection.owner}/${props.selection.repo}`} onEdit={props.onChangeSource} editLabel="Change" />
      <ReviewRow colors={props.colors} label="Branch" value={props.selection.branch} onEdit={props.onChangeSource} editLabel="Change" />
      {props.error && (
        <div style={{ marginTop: '12px', fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444' }}>{props.error}</div>
      )}
      <div className="vx-action-row" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <SecondaryButton colors={props.colors} onClick={props.onEditDetails} disabled={props.building}>Edit details</SecondaryButton>
        <PrimaryButton onClick={props.onDeploy} disabled={props.building} isDark={props.isDark}>
          {props.building ? 'Deploying…' : `Deploy ${props.name}`}
        </PrimaryButton>
      </div>
    </div>
  );
}

/** Single labeled row inside the review card (duplicates DeployFormCards' ReviewRow to stay contained). */
function ReviewRow(props: {
  colors: Colors;
  label: string;
  value: string;
  onEdit: () => void;
  editLabel?: string;
}): React.JSX.Element {
  return (
    <div className="vx-review-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', alignItems: 'center', gap: '12px', padding: '6px 0' }}>
      <div style={{ fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: props.colors.textSecondary }}>
        {props.label}
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: props.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {props.value}
      </div>
      <button onClick={props.onEdit} style={{ border: 'none', background: 'transparent', color: '#01A9ED', fontFamily: FONTS.body, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
        {props.editLabel ?? 'Edit'}
      </button>
    </div>
  );
}
